import { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const DIAS_MAP = { 0:'Domingo', 1:'Lunes', 2:'Martes', 3:'Miercoles', 4:'Jueves', 5:'Viernes', 6:'Sabado' };
function toMin(t) { const [h, m] = t.split(':').map(Number); return h * 60 + m; }

export const FORM_VACIO = {
  medico_id: '',
  paciente_dni: '',
  paciente_id: null,
  paciente_nuevo: null,
  paciente_nombre: '',
  paciente_email: '',
  paciente_telefono: '',
  paciente_obra_social: '',
  paciente_numero_afiliado: '',
  paciente_fecha_nacimiento: '',
  paciente_direccion: '',
  motivo: '',
  fecha: '',
  hora_inicio: '09:00',
  duracion: '30',
  estado: 'pendiente',
  notas: '',
  mensaje_paciente: '',
  nota_medico: '',
  sobreturno: 0,
  especial: 0,
  motivo_especial: '',
  coseguro_estado: null,
  coseguro_monto: '',
  coseguro_medio_pago: 'Efectivo',
  coseguro_registrado_por: '',
  coseguro_fecha: null,
};

const HOY_STR = () => new Date().toISOString().split('T')[0];

// Encapsula la regla de editabilidad de un turno según rol y fecha.
// Hoy ambas coinciden con "fecha >= hoy"; en el futuro podría reemplazarse por un flag de cierre diario.
export function turnoEditable(turno, rol) {
  if (rol === 'admin') return true;
  if (!turno?.fecha) return true; // turno nuevo: permitir
  const fechaTurno = String(turno.fecha).split('T')[0];
  if (rol === 'recepcionista') return fechaTurno >= HOY_STR();
  return false; // médico nunca edita desde este flujo
}

export default function useTurnoForm({ turnoId, onSuccess, disponibilidades = {}, origen = 'TurnoDialog' }) {
  const rolActual = sessionStorage.getItem('rol') || '';
  const puedeHacerSobreturno = ['admin', 'recepcionista', 'medico'].includes(rolActual);

  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [buscandoDni, setBuscandoDni] = useState(false);
  const [estadoDni, setEstadoDni] = useState(null);
  const [error, setError] = useState('');
  const [confirmarSobreturno, setConfirmarSobreturno] = useState(false);
  const [confirmarFueraDeAgenda, setConfirmarFueraDeAgenda] = useState(false);
  const [requiereEmail, setRequiereEmail] = useState(false);
  const [payloadPendiente, setPayloadPendiente] = useState(null);

  function resetForm(initialData = FORM_VACIO) {
    setForm(initialData);
    setError('');
    setEstadoDni(null);
    setConfirmarSobreturno(false);
    setConfirmarFueraDeAgenda(false);
    setRequiereEmail(false);
    setPayloadPendiente(null);
  }

  // Mapea los datos de un turno existente al estado del formulario
  function cargarTurno(t) {
    setForm({
      medico_id: t.medico_id || '',
      paciente_dni: t.paciente_dni || '',
      paciente_id: t.paciente_id || null,
      paciente_nombre: t.paciente_nombre || '',
      paciente_email: t.paciente_email || '',
      paciente_telefono: t.paciente_telefono || '',
      paciente_obra_social: t.obra_social || '',
      paciente_numero_afiliado: t.numero_afiliado || '',
      paciente_fecha_nacimiento: t.fecha_nacimiento ? String(t.fecha_nacimiento).split('T')[0] : '',
      paciente_direccion: t.direccion || '',
      motivo: t.motivo || '',
      fecha: String(t.fecha).split('T')[0],
      hora_inicio: String(t.hora_inicio).substring(0, 5),
      duracion: String(t.duracion || 30),
      estado: t.estado || 'pendiente',
      notas: t.notas || '',
      mensaje_paciente: t.mensaje_paciente || '',
      nota_medico: t.nota_medico || '',
      sobreturno: t.sobreturno || 0,
      especial: t.especial || 0,
      motivo_especial: t.motivo_especial || '',
      coseguro_estado: t.coseguro_estado || null,
      coseguro_monto: t.coseguro_monto != null ? String(t.coseguro_monto) : '',
      coseguro_medio_pago: t.coseguro_medio_pago || 'Efectivo',
      coseguro_registrado_por: t.coseguro_registrado_por || '',
      coseguro_fecha: t.coseguro_fecha || null,
    });
    setEstadoDni(t.paciente_dni ? 'encontrado' : null);
    setError('');

    // Cargar datos permanentes del paciente (fecha_nacimiento, dirección, obra social, afiliado)
    // -- t viene de la fila de la lista (GET /api/turnos?fecha=...), que no hace JOIN con
    // pacientes, así que estos 4 campos siempre llegan undefined en t y hay que rehidratarlos
    // acá. Si a este fetch se le olvida agregar un campo nuevo del paciente, ese campo se ve
    // permanentemente vacío en "Gestionar turno" aunque esté bien guardado en la base.
    const pacienteId = t.paciente_id;
    if (pacienteId) {
      const token = sessionStorage.getItem('token') || '';
      fetch(`${API}/api/pacientes/${pacienteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(p => {
          if (!p) return;
          setForm(f => ({
            ...f,
            paciente_fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).split('T')[0] : '',
            paciente_direccion: p.direccion || '',
            paciente_obra_social: p.obra_social || '',
            paciente_numero_afiliado: p.numero_afiliado || '',
          }));
        })
        .catch(() => {});
    }
  }

  async function buscarPorDni(dni) {
    if (!dni || dni.length < 3) { setEstadoDni(null); return; }
    setBuscandoDni(true);
    try {
      const r = await axios.get(`${API}/api/pacientes/buscar?dni=${encodeURIComponent(dni)}`);
      const p = r.data;
      setForm(f => ({
        ...f,
        paciente_id: p.id,
        paciente_nombre: `${p.apellido}, ${p.nombre}`.trim(),
        paciente_email: p.email || '',
        paciente_telefono: p.telefono || '',
        paciente_obra_social: p.obra_social || '',
        paciente_numero_afiliado: p.numero_afiliado || '',
        paciente_fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).split('T')[0] : '',
        paciente_direccion: p.direccion || '',
      }));
      setEstadoDni('encontrado');
    } catch {
      setForm(f => ({ ...f, paciente_id: null }));
      setEstadoDni('no_encontrado');
    } finally {
      setBuscandoDni(false);
    }
  }

  // Mismo resultado que buscarPorDni pero sin hit a la API (paciente no existe en DB aún)
  function poblarPacienteDesdeCreacion(datos) {
    setForm(f => ({
      ...f,
      paciente_id: null,
      paciente_nombre: `${datos.apellido}, ${datos.nombre}`.trim(),
      paciente_email: datos.email || '',
      paciente_telefono: datos.telefono || '',
      paciente_obra_social: datos.obra_social || '',
      paciente_numero_afiliado: datos.numero_afiliado || '',
      paciente_nuevo: datos,
    }));
    setEstadoDni('encontrado');
  }

  function estaEnAgenda(medicoId, fecha, horaInicio) {
    const horarios = disponibilidades[medicoId] || [];
    if (!horarios.length) return false;
    const diaNom = DIAS_MAP[new Date(fecha + 'T12:00:00').getDay()];
    const bloques = horarios.filter(h => h.dia === diaNom);
    const min = toMin(horaInicio);
    return bloques.some(h => min >= toMin(h.desde) && min < toMin(h.hasta));
  }

  // Duración real configurada para ese médico ese día -- si no hay agenda cargada
  // para ese día, no hay forma de saberlo y se mantiene el default genérico de 30.
  function duracionAgenda(medicoId, fecha) {
    const horarios = disponibilidades[medicoId] || [];
    const diaNom = DIAS_MAP[new Date(fecha + 'T12:00:00').getDay()];
    const bloque = horarios.find(h => h.dia === diaNom);
    return bloque?.duracion || 30;
  }

  function buildPayload(emailOverride) {
    return {
      medico_id: form.medico_id,
      paciente_id: form.paciente_id || null,
      paciente_nuevo: form.paciente_nuevo
        ? { ...form.paciente_nuevo, email: emailOverride ?? form.paciente_nuevo.email }
        : null,
      paciente_dni: form.paciente_dni || '',
      paciente_nombre: form.paciente_nombre,
      paciente_email: emailOverride ?? form.paciente_email,
      paciente_telefono: form.paciente_telefono,
      motivo: form.motivo,
      fecha: form.fecha,
      hora_inicio: form.hora_inicio,
      duracion: parseInt(form.duracion) || 30,
      estado: turnoId ? (form.estado || 'pendiente') : 'pendiente',
      notas: form.notas,
      sobreturno: form.sobreturno || 0,
      especial: form.especial || 0,
      motivo_especial: form.especial ? (form.motivo_especial || '') : null,
      creado_por: turnoId ? undefined : (sessionStorage.getItem('nombre') || ''),
      mensaje_paciente: form.mensaje_paciente || null,
      nota_medico: form.nota_medico || null,
      origen,
    };
  }

  async function enviarTurno(payload) {
    setGuardando(true);
    try {
      let responseData = null;
      if (turnoId) {
        await axios.put(`${API}/api/turnos/${turnoId}`, payload);
      } else {
        const resp = await axios.post(`${API}/api/turnos`, payload);
        responseData = resp.data;
      }
      // Sincronizar los datos del paciente (obra social, número de afiliado, fecha de
      // nacimiento, dirección, etc.) cada vez que se guarda un turno de un paciente ya
      // existente -- antes solo se mandaba al pasar el turno a "En recepción", así que
      // corregir esos datos en cualquier otro estado se perdía en silencio.
      if (form.paciente_id) {
        try {
          // El backend exige nombre y apellido no vacíos en cualquier PUT (política de
          // identificación del paciente) -- este formulario solo tiene el nombre combinado
          // "Apellido, Nombre", así que hay que separarlo antes de mandarlo. Si no se manda,
          // el backend rechaza el guardado completo con 400 y el resto de los campos
          // (incluido el email) se pierde en silencio.
          const [apellidoPaciente, ...restoNombre] = (form.paciente_nombre || '').split(',');
          await axios.put(`${API}/api/pacientes/${form.paciente_id}`, {
            nombre: restoNombre.join(',').trim(),
            apellido: (apellidoPaciente || '').trim(),
            dni: form.paciente_dni,
            telefono: form.paciente_telefono,
            obra_social: form.paciente_obra_social,
            numero_afiliado: form.paciente_numero_afiliado,
            email: form.paciente_email,
            fecha_nacimiento: form.paciente_fecha_nacimiento || null,
            direccion: form.paciente_direccion,
            // Este guardado es un sync incidental de contacto/cobertura desde la gestión del
            // turno, no una edición de identidad -- no debe bloquearse si el paciente (típico
            // en migrados) todavía no tiene fecha de nacimiento cargada.
            soloContacto: true,
          });
        } catch { /* no bloquear el flujo si falla la actualización del paciente */ }
      }
      onSuccess?.(responseData);
    } catch (err) {
      if (err.response?.status === 409 && puedeHacerSobreturno) {
        setPayloadPendiente(payload);
        setConfirmarSobreturno(true);
      } else {
        setError(err.response?.data?.error || 'Error al guardar.');
      }
    } finally {
      setGuardando(false);
    }
  }

  async function guardar(emailOverride) {
    if (!form.medico_id || !form.paciente_nombre || !form.fecha || !form.hora_inicio) {
      setError('Médico, paciente, fecha y hora son obligatorios.'); return;
    }
    if (form.estado === 'en_recepcion') {
      if (!form.paciente_dni?.trim()) { setError('El DNI es obligatorio para recepción.'); return; }
      if (!form.paciente_telefono?.trim()) { setError('El teléfono es obligatorio para recepción.'); return; }
      // obra_social es informativa, no bloquea — paciente puede no tener cobertura
      // coseguro se resuelve en Tab Coseguro después de guardar
    }
    const emailActual = emailOverride ?? form.paciente_email;
    if (!emailActual?.trim()) {
      setRequiereEmail(true);
      return;
    }
    setError('');
    const payload = buildPayload(emailOverride);
    if (!turnoId && puedeHacerSobreturno && !estaEnAgenda(form.medico_id, form.fecha, form.hora_inicio)) {
      setPayloadPendiente(payload);
      setConfirmarFueraDeAgenda(true);
      return;
    }
    await enviarTurno(payload);
  }

  // El email quedó pendiente al confirmar — se guarda en el paciente (si ya existe) y se
  // retoma la confirmación con el valor nuevo, sin que el usuario tenga que repetir la acción.
  async function confirmarEmailYContinuar(email) {
    setRequiereEmail(false);
    if (form.paciente_id) {
      try {
        // PUT /api/pacientes/:id reemplaza el registro completo — traer el paciente actual
        // primero y pisar solo el email, para no perder el resto de sus datos.
        const r = await axios.get(`${API}/api/pacientes/${form.paciente_id}`);
        await axios.put(`${API}/api/pacientes/${form.paciente_id}`, { ...r.data, email });
      } catch { /* el turno igual lleva el email — no bloquear la confirmación por esto */ }
    }
    setForm(f => ({ ...f, paciente_email: email }));
    await guardar(email);
  }

  async function confirmarYGuardarSobreturno() {
    setConfirmarSobreturno(false);
    const p = { ...payloadPendiente, sobreturno: 1 };
    setPayloadPendiente(null);
    setGuardando(true);
    try {
      let responseData = null;
      if (turnoId) {
        await axios.put(`${API}/api/turnos/${turnoId}`, p);
      } else {
        const resp = await axios.post(`${API}/api/turnos`, p);
        responseData = resp.data;
      }
      onSuccess?.(responseData);
    } catch (err) {
      setError(err.response?.data?.error || 'Error.');
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarYGuardarFueraDeAgenda() {
    setConfirmarFueraDeAgenda(false);
    const p = { ...payloadPendiente, sobreturno: 1 };
    setPayloadPendiente(null);
    await enviarTurno(p);
  }

  async function cancelarTurno(motivo = null) {
    if (!turnoId) return;
    try {
      await axios.post(`${API}/api/turnos/${turnoId}/cancelar`, { motivo, origen });
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cancelar el turno.');
    }
  }

  async function eliminar() {
    if (!turnoId) return;
    setEliminando(true);
    try {
      await axios.delete(`${API}/api/turnos/${turnoId}`);
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar.');
    } finally {
      setEliminando(false);
    }
  }

  async function ejecutarCoseguro(tipo, concepto) {
    if (!turnoId) return;
    try {
      await axios.post(`${API}/api/coseguros`, {
        turno_id: turnoId,
        tipo: tipo.toUpperCase(),
        monto: tipo === 'cobrado' ? (form.coseguro_monto || null) : null,
        medio_pago: tipo === 'cobrado' ? form.coseguro_medio_pago : null,
        concepto: concepto || 'Coseguro',
      });
      const now = new Date().toISOString();
      setForm(f => ({
        ...f,
        coseguro_estado: tipo,
        coseguro_monto: tipo === 'cobrado' ? f.coseguro_monto : null,
        coseguro_medio_pago: tipo === 'cobrado' ? f.coseguro_medio_pago : null,
        coseguro_registrado_por: sessionStorage.getItem('nombre') || '',
        coseguro_fecha: now,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrar coseguro.');
    }
  }

  async function corregirCoseguro(motivo) {
    if (!turnoId) return;
    try {
      await axios.post(`${API}/api/coseguros/${turnoId}/corregir`, { motivo });
      setForm(f => ({ ...f, coseguro_estado: null, coseguro_monto: '', coseguro_registrado_por: '', coseguro_fecha: null }));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al corregir coseguro.');
    }
  }

  async function anularCoseguro(motivo) {
    if (!turnoId) return;
    try {
      await axios.post(`${API}/api/coseguros/${turnoId}/anular`, { motivo });
      setForm(f => ({ ...f, coseguro_estado: null, coseguro_monto: null, coseguro_medio_pago: 'Efectivo', coseguro_registrado_por: '', coseguro_fecha: null }));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al anular coseguro.');
    }
  }

  return {
    // estado del formulario
    form, setForm,
    guardando, setGuardando,
    eliminando, setEliminando,
    buscandoDni, estadoDni, setEstadoDni,
    error, setError,
    // roles — puedeEditarCoseguro se deriva de form.fecha para reflejar la regla de fecha
    puedeEditarCoseguro: turnoEditable({ fecha: form.fecha }, rolActual) && ['admin', 'recepcionista'].includes(rolActual),
    puedeHacerSobreturno,
    esAdmin: rolActual === 'admin',
    // confirmaciones de sobreturno (estado para renderizar dialogs de confirmación)
    confirmarSobreturno, setConfirmarSobreturno,
    confirmarFueraDeAgenda, setConfirmarFueraDeAgenda,
    requiereEmail, setRequiereEmail,
    payloadPendiente, setPayloadPendiente,
    // funciones
    resetForm, cargarTurno,
    buscarPorDni, poblarPacienteDesdeCreacion,
    guardar, enviarTurno, confirmarEmailYContinuar,
    confirmarYGuardarSobreturno, confirmarYGuardarFueraDeAgenda,
    cancelarTurno,
    eliminar,
    ejecutarCoseguro, corregirCoseguro, anularCoseguro,
    estaEnAgenda, duracionAgenda,
  };
}
